import { ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatDatepicker } from '@angular/material/datepicker';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppService } from 'src/@core/services/app.service';
import { JobService } from 'src/@core/services/job/job.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { EnterpriseAdminService } from 'src/app/modules/enterprise-admin/enterprise-admin.service';
import { API_SERRI_LIST_TEMPLATES, PROJECT_API_KEY } from 'src/@core/services/util/api.endpoints';
import { CreateTaskPayload, CreateTasksByOutcomePayload, CreateTasksBySignalPayload, TaskListItem, TasksService } from '../services/tasks.service';
import { parseOutcomeNamesFromResponse } from '../utils/entity-leads.utils';
import { SignalStrength } from '../models/post-call-signal.types';
import {
  buildHourOptions,
  buildMinuteOptions,
  combineDateAndTimePartsToIso,
  formatScheduleDateLabel,
  splitIsoToDateAndTime,
  time24ToParts,
  TimeMeridiem,
} from '../utils/datetime.util';
import {
  findTeamMemberByUserId,
  getTeamMemberDisplayName,
  getTeamMemberUserId,
} from '../utils/team-member.util';

/** Bulk create scope: match by outcome name or by signal strength. */
export type CreateTaskScope = 'outcome' | 'signal';

const SIGNAL_STRENGTH_OPTIONS: SignalStrength[] = [
  'Strong',
  'Good',
  'Weak',
  'No signal',
  'Excluded',
];

/** One lead to plan a task for, instead of every lead in an outcome. */
export interface CreateTaskLeadContext {
  peopleId: string;
  name?: string;
  number?: string;
  outcome?: string;
  photoUrl?: string;
  /** Lead's stored next-call prompt (`leads.task_prompt`) for prefills / BE fallback. */
  taskPrompt?: string;
}

export interface CreateTaskQuickPreset {
  icon: string;
  label: string;
  owner: 'ai' | 'human';
  channel: string;
  title: string;
}

/** Optional values seeded when opening from a post-call signal's task_prompt. */
export interface CreateTaskDialogPreset {
  title?: string;
  instructions?: string;
  channel?: string;
  mode?: 'ai' | 'human';
}

export interface CreateTaskDialogData {
  mode: 'ai' | 'human' | 'edit';
  entityId: string;
  projects: any[];
  defaultProjectId?: string | null;
  defaultOutcome?: string | null;
  outcomeOptions?: string[];
  editTask?: TaskListItem;
  lead?: CreateTaskLeadContext;
  /** Prefill title / prompt / channel (e.g. from a signal task_prompt). */
  preset?: CreateTaskDialogPreset;
}

@Component({
  selector: 'hour4u-admin-create-task-dialog',
  templateUrl: './create-task-dialog.component.html',
  styleUrls: ['./create-task-dialog.component.scss']
})
export class CreateTaskDialogComponent implements OnInit {
  @ViewChild('taskDatePicker', { static: false }) taskDatePicker: MatDatepicker<Date>;

  mode: 'ai' | 'human' | 'edit' = 'ai';
  editTaskId = '';
  isLoading = false;
  isLoadingLeads = false;
  isLoadingTemplates = false;
  isLoadingOutcomes = false;

  projectId = '';
  outcome = '';
  /** Outcomes vs Signals for bulk create (ignored in lead / edit modes). */
  createScope: CreateTaskScope = 'outcome';
  signalStrengthOptions = SIGNAL_STRENGTH_OPTIONS;
  selectedStrengths: SignalStrength[] = ['Strong', 'Good'];
  peopleId = '';
  leadName = '';
  leadNumber = '';
  title = '';
  scheduledDate: Date | null = null;
  timeHour = '09';
  timeMinute = '00';
  timeMeridiem: TimeMeridiem = 'AM';
  hours = buildHourOptions();
  minutes = buildMinuteOptions();
  channel = 'call';
  ownerId = '';
  ownerName = '';
  instructions = '';
  runImmediately = true;
  disableExistingTasks = true;
  templateId = '';
  templateName = '';

  projects: any[] = [];
  leads: any[] = [];
  teamMembers: any[] = [];
  templates: any[] = [];
  outcomeOptions: string[] = [];
  isLoadingProjects = false;

  channelOptions = [
    { value: 'call', label: 'Call' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'email', label: 'Email' },
    { value: 'human', label: 'Human' },
  ];

  quickPresets: CreateTaskQuickPreset[] = [
    { icon: 'call', label: 'Call back now', owner: 'ai', channel: 'call', title: 'Follow-up call' },
    { icon: 'chat', label: 'WhatsApp follow-up', owner: 'ai', channel: 'whatsapp', title: 'WhatsApp follow-up' },
    { icon: 'person', label: 'Human follow-up', owner: 'human', channel: 'human', title: 'Personal follow-up' },
  ];
  activePresetLabel = '';

  constructor(
    private dialogRef: MatDialogRef<CreateTaskDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateTaskDialogData,
    private tasksService: TasksService,
    private jobService: JobService,
    private enterpriseAdminService: EnterpriseAdminService,
    private appService: AppService,
    private notify: NotificationService,
    private http: HttpClient,
    private cd: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    if (this.data && this.data.editTask) {
      this.mode = 'edit';
      this.projects = Array.isArray(this.data.projects) ? [...this.data.projects] : [];
      this.prefillFromTask(this.data.editTask);
      if (this.isHumanTask) {
        this.loadTeamMembers();
      }
      if (this.channel === 'whatsapp') {
        this.loadTemplates();
      }
      return;
    }

    this.mode = (this.data && this.data.mode) || 'ai';
    this.projects = Array.isArray(this.data && this.data.projects) ? [...this.data.projects] : [];
    this.projectId = (this.data && this.data.defaultProjectId) || '';
    this.outcome = (this.data && this.data.defaultOutcome) || '';
    this.outcomeOptions = Array.isArray(this.data && this.data.outcomeOptions)
      ? [...this.data.outcomeOptions]
      : [];
    this.applyDialogPreset(this.data && this.data.preset);
    // If no explicit preset, seed AI instructions from the lead's task_prompt.
    if (!(this.data && this.data.preset && this.data.preset.instructions) && !this.instructions) {
      const leadPrompt =
        (this.data && this.data.lead && this.data.lead.taskPrompt) || '';
      if (leadPrompt.trim()) {
        this.instructions = leadPrompt.trim();
        if (!this.title.trim()) {
          this.title = leadPrompt.trim().length <= 72
            ? leadPrompt.trim()
            : `${leadPrompt.trim().slice(0, 69)}…`;
        }
      }
    }
    if (this.mode === 'human') {
      this.loadTeamMembers();
    }
    if (this.isLeadMode) {
      // The lead fixes both project and outcome, so there is nothing to load.
      const lead = this.data.lead;
      this.peopleId = lead.peopleId;
      this.leadName = lead.name || '';
      this.leadNumber = lead.number || '';
      return;
    }
    if (!this.isEditMode) {
      this.bootstrapCreateMode();
    }
  }

  /** Seed fields from a signal (or other caller) without overriding edit mode. */
  private applyDialogPreset(preset: CreateTaskDialogPreset | null | undefined): void {
    if (!preset) {
      return;
    }
    if (preset.mode === 'ai' || preset.mode === 'human') {
      this.mode = preset.mode;
    }
    if (preset.channel) {
      this.channel = preset.channel;
    }
    if (preset.title) {
      this.title = preset.title;
    }
    if (preset.instructions) {
      this.instructions = preset.instructions;
    }
  }

  private bootstrapCreateMode() {
    if (this.projects.length) {
      this.ensureProjectSelected();
      this.loadOutcomes();
      return;
    }
    this.loadProjectsForDialog();
  }

  private loadProjectsForDialog() {
    if (!this.data || !this.data.entityId) {
      return;
    }

    this.isLoadingProjects = true;
    this.jobService.getAllProjects(this.data.entityId, 1, 100, 'created_at', 'desc', []).subscribe(
      (res: any) => {
        this.isLoadingProjects = false;
        this.projects = (res && res.items) || [];
        this.ensureProjectSelected();
        this.loadOutcomes();
        this.cd.markForCheck();
      },
      () => {
        this.isLoadingProjects = false;
        this.projects = [];
        this.loadOutcomes();
        this.cd.markForCheck();
      }
    );
  }

  private ensureProjectSelected() {
    if (this.projectId) {
      return;
    }
    const firstProject = this.projects.find((project) => project && project.project_id);
    if (firstProject) {
      this.projectId = firstProject.project_id;
    }
  }

  get dialogTitle(): string {
    if (this.isEditMode) {
      return 'Edit task';
    }
    if (this.isLeadMode) {
      return 'New task for this lead';
    }
    return this.mode === 'ai' ? 'New AI tasks' : 'New human tasks';
  }

  get isLeadMode(): boolean {
    return !this.isEditMode && !!(this.data && this.data.lead && this.data.lead.peopleId);
  }

  get isBulkCreateMode(): boolean {
    return !this.isEditMode && !this.isLeadMode;
  }

  get isSignalScope(): boolean {
    return this.isBulkCreateMode && this.createScope === 'signal';
  }

  get isOutcomeScope(): boolean {
    return this.isBulkCreateMode && this.createScope === 'outcome';
  }

  get submitLabel(): string {
    if (this.isEditMode) {
      return 'Save changes';
    }
    return this.isLeadMode ? 'Create task' : 'Create tasks';
  }

  get taskPromptPlaceholder(): string {
    if (this.isSignalScope) {
      return 'Leave blank to use each lead’s task prompt from signal analysis';
    }
    if (this.isOutcomeScope) {
      return 'Optional — leave blank to use each lead’s task prompt when available';
    }
    return 'e.g. Confirm their interest and ask for preferred meeting time';
  }

  setCreateScope(scope: CreateTaskScope) {
    if (this.createScope === scope) {
      return;
    }
    this.createScope = scope;
  }

  isStrengthSelected(strength: SignalStrength): boolean {
    return this.selectedStrengths.indexOf(strength) !== -1;
  }

  toggleStrength(strength: SignalStrength) {
    if (this.isStrengthSelected(strength)) {
      this.selectedStrengths = this.selectedStrengths.filter((s) => s !== strength);
      return;
    }
    this.selectedStrengths = [...this.selectedStrengths, strength];
  }

  get leadInitial(): string {
    const name = (this.leadName || '').trim();
    return name ? name.charAt(0).toUpperCase() : '#';
  }

  get leadOutcome(): string {
    return (this.data && this.data.lead && this.data.lead.outcome) || '';
  }

  get leadPhotoUrl(): string {
    return (this.data && this.data.lead && this.data.lead.photoUrl) || '';
  }

  setOwnerMode(mode: 'ai' | 'human') {
    if (this.mode === mode) {
      return;
    }
    this.mode = mode;
    this.activePresetLabel = '';
    if (mode === 'human' && !this.teamMembers.length) {
      this.loadTeamMembers();
    }
  }

  applyPreset(preset: CreateTaskQuickPreset) {
    this.setOwnerMode(preset.owner);
    if (this.channel !== preset.channel) {
      this.channel = preset.channel;
      this.onChannelChange();
    }
    this.title = preset.title;
    this.activePresetLabel = preset.label;
  }

  get outcomeSelectOptions(): string[] {
    return this.outcomeOptions;
  }

  get outcomeDropdownOptions(): Array<{ value: string; label: string }> {
    return this.outcomeOptions.map((name) => ({ value: name, label: name }));
  }

  get outcomePlaceholder(): string {
    if (this.isLoadingOutcomes) {
      return 'Loading outcomes…';
    }
    if (!this.projectId) {
      return this.isLoadingProjects ? 'Loading projects…' : 'Select a project first';
    }
    return this.outcomeOptions.length ? 'Select outcome' : 'No outcomes available';
  }

  get isEditMode(): boolean {
    return this.mode === 'edit' || !!(this.data && this.data.editTask);
  }

  get isAiTask(): boolean {
    if (this.isEditMode && this.data && this.data.editTask) {
      return (this.data.editTask.owner_type || '').toLowerCase() === 'ai';
    }
    return this.mode === 'ai';
  }

  get isHumanTask(): boolean {
    if (this.isEditMode && this.data && this.data.editTask) {
      const t = (this.data.editTask.owner_type || '').toLowerCase();
      return t === 'human' || t === 'unassigned';
    }
    return this.mode === 'human';
  }

  private prefillFromTask(task: TaskListItem) {
    this.editTaskId = task.task_id;
    this.projectId = task.project_id || '';
    this.peopleId = task.people_id || '';
    this.leadName = task.lead_name || '';
    this.leadNumber = task.lead_number || '';
    this.title = task.title || '';
    this.channel = task.channel || 'call';
    this.instructions = task.instructions || '';
    this.templateId = task.template_id || '';
    this.templateName = task.template_name || '';
    this.ownerId = task.owner_id || '';
    this.ownerName = task.owner_name || '';
    if (task.scheduled_at) {
      const split = splitIsoToDateAndTime(task.scheduled_at);
      this.scheduledDate = split.date;
      const parts = time24ToParts(split.time);
      this.timeHour = parts.hour;
      this.timeMinute = parts.minute;
      this.timeMeridiem = parts.meridiem;
    }
    if (this.projectId && this.peopleId) {
      this.leads = [{
        people_id: this.peopleId,
        client_name: this.leadName,
        client_number: this.leadNumber,
      }];
    }
  }

  get assignLabel(): string {
    return this.mode === 'ai' ? 'Arya (AI)' : 'Assign to';
  }

  onProjectChange() {
    if (this.isEditMode) {
      this.peopleId = '';
      this.leadName = '';
      this.leadNumber = '';
      this.loadLeads();
    } else {
      this.loadOutcomes();
    }
    if (this.channel === 'whatsapp') {
      this.loadTemplates();
    }
  }

  loadOutcomes() {
    if (this.isEditMode || !this.data || !this.data.entityId) {
      return;
    }

    this.isLoadingOutcomes = true;

    if (this.projectId) {
      this.jobService.getHoaProject(this.data.entityId, this.projectId).subscribe(
        (res) => {
          const names = parseOutcomeNamesFromResponse(res);
          if (names.length) {
            this.isLoadingOutcomes = false;
            this.outcomeOptions = names;
            this.syncSelectedOutcome();
            this.cd.markForCheck();
            return;
          }
          this.loadEntityOutcomes();
        },
        () => {
          this.loadEntityOutcomes();
        }
      );
      return;
    }

    this.loadEntityOutcomes();
  }

  private loadEntityOutcomes() {
    if (!this.data || !this.data.entityId) {
      this.isLoadingOutcomes = false;
      this.outcomeOptions = [];
      return;
    }

    this.isLoadingOutcomes = true;
    this.jobService.getHoaEntityOutcomesFilter(this.data.entityId).subscribe(
      (res) => {
        this.isLoadingOutcomes = false;
        const parsed = parseOutcomeNamesFromResponse(res);
        if (parsed.length) {
          this.outcomeOptions = parsed;
        } else {
          const list = res && Array.isArray(res.outcomes) ? res.outcomes : [];
          const seen = new Set<string>();
          this.outcomeOptions = list
            .map((value: any) => (typeof value === 'string' ? value.trim() : ''))
            .filter((name: string) => {
              if (!name || seen.has(name)) {
                return false;
              }
              seen.add(name);
              return true;
            });
        }
        this.syncSelectedOutcome();
        this.cd.markForCheck();
      },
      () => {
        this.isLoadingOutcomes = false;
        this.outcomeOptions = [];
        this.outcome = '';
        this.cd.markForCheck();
      }
    );
  }

  private syncSelectedOutcome() {
    if (this.outcome && this.outcomeOptions.includes(this.outcome)) {
      return;
    }
    const preferred = (this.data && this.data.defaultOutcome) || '';
    if (preferred && this.outcomeOptions.includes(preferred)) {
      this.outcome = preferred;
      return;
    }
    this.outcome = this.outcomeOptions.length ? this.outcomeOptions[0] : '';
  }

  onLeadChange(peopleId: string) {
    const lead = this.leads.find((l) => l.people_id === peopleId);
    if (lead) {
      this.leadName = lead.client_name || '';
      this.leadNumber = lead.client_number || '';
    }
  }

  onChannelChange() {
    this.templateId = '';
    this.templateName = '';
    if (this.channel === 'whatsapp') {
      this.loadTemplates();
    }
  }

  onOwnerChange(userId: string) {
    if (!userId) {
      this.ownerId = '';
      this.ownerName = 'Unassigned';
      return;
    }
    const member = findTeamMemberByUserId(this.teamMembers, userId);
    this.ownerId = userId;
    this.ownerName = member ? getTeamMemberDisplayName(member) : '';
  }

  getTeamMemberLabel(member: any): string {
    return getTeamMemberDisplayName(member);
  }

  getTeamMemberValue(member: any): string {
    return getTeamMemberUserId(member);
  }

  onTemplateChange(templateId: string) {
    const tpl = this.templates.find((t) => (t.id || t.template_id) === templateId);
    this.templateId = templateId;
    this.templateName = tpl ? (tpl.name || tpl.template_name || '') : '';
  }

  loadLeads() {
    if (!this.data.entityId || !this.projectId) {
      this.leads = [];
      return;
    }
    this.isLoadingLeads = true;
    this.enterpriseAdminService.getCallHistoryProject(
      this.data.entityId,
      this.projectId,
      100,
      1
    ).subscribe(
      (res: any) => {
        this.isLoadingLeads = false;
        const rows = (res && res.data) || [];
        const seen = new Set<string>();
        this.leads = rows.filter((row: any) => {
          const id = row.people_id;
          if (!id || seen.has(id)) {
            return false;
          }
          seen.add(id);
          return true;
        });
      },
      () => {
        this.isLoadingLeads = false;
        this.leads = [];
      }
    );
  }

  loadTeamMembers() {
    const profile = this.appService.getCurrentAccount();
    const entityId = profile && profile.entity && profile.entity.id;
    if (!entityId) {
      return;
    }
    this.jobService.searchAndSortTeamMembersv2(entityId, 0, 100, 'createdOn', 'desc', []).subscribe(
      (res: any) => {
        this.teamMembers = (res && res.content) || [];
      },
      () => {
        this.teamMembers = [];
      }
    );
  }

  loadTemplates() {
    if (!this.data.entityId || !this.projectId) {
      return;
    }

    this.isLoadingTemplates = true;
    this.templates = [];
    this.templateId = '';
    this.templateName = '';

    this.resolveWhatsappPhoneNumber().subscribe((phoneNumber) => {
      if (!phoneNumber) {
        this.isLoadingTemplates = false;
        this.templates = [];
        this.notify.showMessage('WhatsApp phone number not found for this project.', 3000);
        return;
      }
      this.fetchSerriTemplates(this.data.entityId, phoneNumber);
    });
  }

  private resolveWhatsappPhoneNumber(): Observable<string> {
    const project = this.projects.find((p) => p.project_id === this.projectId);
    const fromList = project && (project.whatsapp_phone_number || project.phone_number);
    if (fromList) {
      return of(String(fromList).trim());
    }

    return this.jobService.getHoaProject(this.data.entityId, this.projectId).pipe(
      map((res: any) => {
        const phone = res && (res.whatsapp_phone_number || res.phone_number);
        return phone ? String(phone).trim() : '';
      }),
      catchError(() => of(''))
    );
  }

  private fetchSerriTemplates(entityId: string, whatsappPhoneNumber: string) {
    const params = new URLSearchParams({
      entity_id: entityId,
      whatsapp_phone_number: whatsappPhoneNumber,
      page: '1',
      limit: '100',
    });

    const authToken = localStorage.getItem('jwtToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'Authorization': `Bearer ${authToken}`,
      'x-api-key': PROJECT_API_KEY,
      'x-suppress-error': 'true',
    });

    const url = `${API_SERRI_LIST_TEMPLATES}?${params.toString()}`;
    this.http.get<any>(url, { headers }).subscribe(
      (response) => {
        this.isLoadingTemplates = false;
        this.templates = this.parseSerriTemplates(response);
      },
      () => {
        this.isLoadingTemplates = false;
        this.templates = [];
        this.notify.showMessage('Failed to load WhatsApp templates.', 3000);
      }
    );
  }

  private parseSerriTemplates(response: any): any[] {
    let raw: any[] = [];

    if (response && response.success && response.data && response.data.data && Array.isArray(response.data.data)) {
      raw = response.data.data;
    } else if (response && response.success && response.data && Array.isArray(response.data)) {
      raw = response.data;
    } else if (Array.isArray(response)) {
      raw = response;
    } else if (response && Array.isArray(response.templates)) {
      raw = response.templates;
    }

    return raw
      .filter((template: any) => template.status === 'APPROVED' || !template.status)
      .map((template: any) => ({
        ...template,
        id: template.id || template.template_id,
        template_id: template.id || template.template_id,
        name: template.name || template.label || template.template_name || 'Template',
      }));
  }

  get scheduledDateLabel(): string {
    return formatScheduleDateLabel(this.scheduledDate);
  }

  openDatePicker() {
    if (this.taskDatePicker) {
      this.taskDatePicker.open();
    }
  }

  close() {
    this.dialogRef.close();
  }

  private resolveTaskType(channel: string): string {
    const c = (channel || '').toLowerCase();
    if (c === 'call') {
      return 'scheduled_call';
    }
    if (c === 'whatsapp') {
      return 'scheduled_whatsapp';
    }
    if (c === 'email') {
      return 'scheduled_email';
    }
    if (c === 'human') {
      return 'human_task';
    }
    return 'scheduled_task';
  }

  create() {
    if (this.isEditMode && this.editTaskId) {
      if (!this.title.trim()) {
        this.notify.showMessage('Task title is required.', 3000);
        return;
      }
    } else if (this.isLeadMode) {
      if (!this.projectId || !this.title.trim()) {
        this.notify.showMessage('Task title is required.', 3000);
        return;
      }
    } else if (this.isSignalScope) {
      if (!this.projectId || !this.title.trim() || !this.selectedStrengths.length) {
        this.notify.showMessage('Project, at least one signal strength, and task title are required.', 3000);
        return;
      }
    } else if (!this.projectId || !this.outcome || !this.title.trim()) {
      this.notify.showMessage('Project, outcome, and task title are required.', 3000);
      return;
    }

    const scheduledIso = this.isEditMode
      ? combineDateAndTimePartsToIso(this.scheduledDate, {
        hour: this.timeHour,
        minute: this.timeMinute,
        meridiem: this.timeMeridiem,
      })
      : (this.runImmediately
        ? 'now'
        : combineDateAndTimePartsToIso(this.scheduledDate, {
          hour: this.timeHour,
          minute: this.timeMinute,
          meridiem: this.timeMeridiem,
        }));

    if (this.isEditMode && this.editTaskId) {
      const updatePayload: Record<string, any> = {
        task_id: this.editTaskId,
        title: this.title.trim(),
        scheduled_at: scheduledIso,
        channel: this.channel,
        instructions: this.instructions || undefined,
        template_id: this.templateId || undefined,
        template_name: this.templateName || undefined,
      };
      if (this.isHumanTask) {
        updatePayload.owner_type = this.ownerId ? 'human' : 'unassigned';
        updatePayload.owner_id = this.ownerId || undefined;
        updatePayload.owner_name = this.ownerName || undefined;
      }
      this.isLoading = true;
      this.tasksService.updateTask(updatePayload).subscribe(
        () => {
          this.isLoading = false;
          this.notify.showMessage('Task updated successfully!', 2000);
          this.dialogRef.close(true);
        },
        (err) => {
          this.isLoading = false;
          const msg = (err && err.error && (err.error.error || err.error.message)) || 'Failed to update task.';
          this.notify.showMessage(msg, 3000);
        }
      );
      return;
    }

    if (this.isLeadMode) {
      this.createForLead(scheduledIso);
      return;
    }

    if (this.isSignalScope) {
      this.createBySignal(scheduledIso);
      return;
    }

    this.createByOutcome(scheduledIso);
  }

  private buildBulkOwnerFields(): {
    owner_type: string;
    owner_id?: string;
    owner_name?: string;
  } {
    if (this.mode === 'ai') {
      return { owner_type: 'ai', owner_id: 'arya', owner_name: 'Arya' };
    }
    return {
      owner_type: 'ai',
      owner_id: this.ownerId || undefined,
      owner_name: this.ownerName || undefined,
    };
  }

  private createByOutcome(scheduledIso: string | undefined) {
    const owner = this.buildBulkOwnerFields();
    const payload: CreateTasksByOutcomePayload = {
      entity_id: this.data.entityId,
      project_id: this.projectId,
      outcome: this.outcome.trim(),
      title: this.title.trim(),
      channel: this.channel,
      task_type: this.resolveTaskType(this.channel),
      scheduled_at: scheduledIso,
      // If set, BE uses this for every lead; if omitted, each lead's task_prompt.
      task_prompt: this.instructions.trim() || undefined,
      disable_existing_tasks: this.disableExistingTasks,
      owner_type: owner.owner_type,
      owner_id: owner.owner_id,
      owner_name: owner.owner_name,
    };

    this.isLoading = true;
    this.tasksService.createTasksByOutcome(payload).subscribe(
      (res: any) => {
        this.isLoading = false;
        const created = res && (res.created_count != null ? res.created_count : res.tasks_created);
        const msg = created != null
          ? `Created ${created} task(s) successfully!`
          : 'Tasks created successfully!';
        this.notify.showMessage(msg, 2000);
        this.dialogRef.close(true);
      },
      (err) => {
        this.isLoading = false;
        const msg = (err && err.error && (err.error.error || err.error.message)) || 'Failed to create tasks.';
        this.notify.showMessage(msg, 3000);
      }
    );
  }

  /** POST /createTasksBySignal — match leads by latest signal.strength. */
  private createBySignal(scheduledIso: string | undefined) {
    const owner = this.buildBulkOwnerFields();
    const payload: CreateTasksBySignalPayload = {
      entity_id: this.data.entityId,
      project_id: this.projectId,
      strengths: [...this.selectedStrengths],
      title: this.title.trim(),
      channel: this.channel,
      task_type: this.resolveTaskType(this.channel),
      scheduled_at: scheduledIso,
      // Blank → BE uses each lead's leads.task_prompt.
      task_prompt: this.instructions.trim() || undefined,
      disable_existing_tasks: this.disableExistingTasks,
      owner_type: owner.owner_type,
      owner_id: owner.owner_id,
      owner_name: owner.owner_name,
    };

    this.isLoading = true;
    this.tasksService.createTasksBySignal(payload).subscribe(
      (res: any) => {
        this.isLoading = false;
        const created = res && (res.created_count != null ? res.created_count : res.tasks_created);
        const matched = res && res.matched_leads;
        let msg = created != null
          ? `Created ${created} task(s) successfully!`
          : 'Tasks created successfully!';
        if (matched != null && created != null && matched !== created) {
          msg = `Matched ${matched} lead(s), created ${created} task(s).`;
        }
        this.notify.showMessage(msg, 2500);
        this.dialogRef.close(true);
      },
      (err) => {
        this.isLoading = false;
        const msg = (err && err.error && (err.error.error || err.error.message)) || 'Failed to create tasks by signal.';
        this.notify.showMessage(msg, 3000);
      }
    );
  }

  /**
   * POST /createTask for the one lead. /createTask parks AI call/WhatsApp/email
   * tasks as awaiting approval, so an AI task is approved straight after: that
   * is the same execute path /createTasksByOutcome takes on the server.
   */
  private createForLead(scheduledIso: string | undefined) {
    if (!scheduledIso) {
      this.notify.showMessage('Pick a date and time, or run it immediately.', 3000);
      return;
    }

    const isAi = this.mode === 'ai';
    // Request task_prompt if the operator set instructions; otherwise omit so
    // BE falls back to leads.task_prompt (create_manual_task resolution).
    const instructions = this.instructions.trim();
    const leadFallback =
      (this.data && this.data.lead && this.data.lead.taskPrompt) || '';
    const resolvedPrompt = instructions || leadFallback.trim() || undefined;
    const payload: CreateTaskPayload = {
      entity_id: this.data.entityId,
      project_id: this.projectId,
      people_id: this.peopleId,
      title: this.title.trim(),
      channel: this.channel,
      task_type: this.resolveTaskType(this.channel),
      scheduled_at: scheduledIso,
      owner_type: isAi ? 'ai' : 'human',
      owner_id: isAi ? 'arya' : (this.ownerId || undefined),
      owner_name: isAi ? 'Arya' : (this.ownerName || undefined),
      instructions: (isAi && resolvedPrompt) || undefined,
      task_prompt: (isAi && resolvedPrompt) || undefined,
      template_id: this.templateId || undefined,
      template_name: this.templateName || undefined,
      lead_name: this.leadName || undefined,
      lead_number: this.leadNumber || undefined,
    };

    this.isLoading = true;
    this.tasksService.createTask(payload).subscribe(
      (res: any) => {
        const taskId = res && res.task_id;
        if (!isAi || !taskId) {
          this.isLoading = false;
          this.notify.showMessage('Task created successfully!', 2000);
          this.dialogRef.close(true);
          return;
        }
        this.tasksService.approveTask(taskId, { scheduled_at: scheduledIso }).subscribe(
          () => {
            this.isLoading = false;
            this.notify.showMessage('Task created. Arya will take it from here.', 2500);
            this.dialogRef.close(true);
          },
          (err) => {
            this.isLoading = false;
            const reason = (err && err.error && (err.error.error || err.error.message)) || 'approval failed';
            this.notify.showMessage(
              `Task created but still awaiting approval (${reason}). Approve it from the Tasks page.`,
              5000
            );
            this.dialogRef.close(true);
          }
        );
      },
      (err) => {
        this.isLoading = false;
        const msg = (err && err.error && (err.error.error || err.error.message)) || 'Failed to create task.';
        this.notify.showMessage(msg, 3000);
      }
    );
  }
}
